<script>
  import { createEventDispatcher } from 'svelte';

  import Add16 from 'carbon-icons-svelte/lib/Add16';
  import { Button, DataTable, ListItem, Loading, Toolbar, ToolbarContent, UnorderedList } from 'carbon-components-svelte';
  import BulkAddModal from './BulkAddModal.svelte';
  import PaginationWithRouting from '../../../../components/PaginationWithRouting.svelte';
  import ToolbarSearchWithRouting from '../../../../components/ToolbarSearchWithRouting.svelte';

  import { DataInventoryServiceIds } from '../../../../../shared/constants';
  import { getPathDetails } from '../../../../services';
  import { rowContainsText } from '../../../../utils';

  export let feedInventory = [],
    serviceId;

  const dispatch = createEventDispatcher();

  let bulkAddModalOpen = false,
    query = { pageSize: 5, currentPage: 1, searchText: '' },
    datesState = { dataPerFeed: {}, dataPerPath: {} };

  const headers = [{ key: 'name', value: 'Feed Name' }];

  headers.push(
    ...(serviceId === DataInventoryServiceIds.AWS_RAW_FEEDS
      ? [{ key: 'actions', empty: true, sort: false }]
      : [
          { key: 'availability', value: 'Availability', sort: false },
          { key: 'actions', empty: true, sort: false },
        ])
  );

  async function handleExpandRow(event) {
    const { row, expanded } = event.detail;
    const rowId = row?.id;
    const feedState = rowId !== undefined && rowId !== null ? datesState.dataPerFeed?.[rowId] : null;

    if (!expanded || rowId === undefined || rowId === null || !row.paths?.length || feedState?.loading || (feedState && !feedState.error)) {
      return;
    }

    datesState = { ...datesState, dataPerFeed: { ...datesState.dataPerFeed, [rowId]: { loading: true, error: null } } };

    try {
      const pathDetails = await Promise.all(row.paths.map((path) => getPathDetails(path)));
      const nextDataPerPath = Object.fromEntries(pathDetails.map(({ path, startDate, endDate }) => [path, { startDate, endDate }]));

      datesState = {
        ...datesState,
        dataPerFeed: {
          ...datesState.dataPerFeed,
          [rowId]: { loading: false, error: null },
        },
        dataPerPath: {
          ...datesState.dataPerPath,
          ...nextDataPerPath,
        },
      };
    } catch (error) {
      console.error('[FeedInventoryTable] Failed to load path details for feed', row.feedId, error);
      datesState = {
        ...datesState,
        dataPerFeed: {
          ...datesState.dataPerFeed,
          [rowId]: { loading: false, error: 'Failed to load path dates. Please refresh and retry.' },
        },
      };
    }
  }

  const onSearchTextChange = (e) => {
    query = { ...query, currentPage: 1, searchText: e?.detail ?? '' };
  };

  $: filteredFeedInventory = query.searchText ? feedInventory.filter((feed) => rowContainsText(feed, query.searchText)) : feedInventory;
</script>

<h3 class="section-heading h5">Data Inventory</h3>
<DataTable
  class="mst-table--lock-end"
  {headers}
  sortable
  size="short"
  expandable={serviceId === DataInventoryServiceIds.AWS_RAW_FEEDS}
  pageSize={query.pageSize}
  page={query.currentPage}
  rows={filteredFeedInventory}
  on:click:row--expand={handleExpandRow}
>
  <span slot="cell" let:cell let:row>
    {#if cell.key === 'actions'}
      <Button
        hideTooltip
        kind="tertiary"
        size="small"
        iconDescription="Add entitlement"
        icon={Add16}
        tooltipPosition="left"
        on:click={() => dispatch('add-feeds', { feeds: [row], options: { persistSelectedRanges: false } })}
      />
    {:else if cell.key === 'availability'}
      <div class="availability">
        {row.availabilityStartMonth ?? 'N/A'} &mdash; {row.availabilityEndMonth ?? 'N/A'}
      </div>
    {:else}
      {cell.value}
    {/if}
  </span>
  <div class="expanded-row" slot="expandedRow" let:row>
    <UnorderedList>
      {#each row.paths as path}
        <ListItem>
          <code class="path">
            <div>{path}</div>
            {#if datesState.dataPerFeed?.[row.id]?.loading}
              <Loading withOverlay={false} small />
            {:else if datesState.dataPerFeed?.[row.id]?.error}
              <em class="error">{datesState.dataPerFeed[row.id].error}</em>
            {:else if datesState.dataPerPath?.[path]}
              <div>
                <strong>Start Date:</strong>
                {datesState.dataPerPath[path].startDate ?? 'N/A'}
                <strong>End Date:</strong>
                {datesState.dataPerPath[path].endDate ?? 'N/A'}
              </div>
            {/if}
          </code>
        </ListItem>
      {/each}
    </UnorderedList>
  </div>
  <Toolbar size="sm">
    <ToolbarContent>
      <ToolbarSearchWithRouting
        persistent
        placeholder="Search feeds..."
        bind:searchText={query.searchText}
        on:toolbarSearchValueChange={onSearchTextChange}
      />
      <Button kind="secondary" size="small" on:click={() => (bulkAddModalOpen = true)}>Bulk add feeds</Button>
    </ToolbarContent>
  </Toolbar>
</DataTable>
<PaginationWithRouting
  bind:page={query.currentPage}
  bind:pageSize={query.pageSize}
  pageSizes={[5, 10, 25, 50]}
  totalItems={filteredFeedInventory.length}
/>

<BulkAddModal {feedInventory} bind:bulkAddModalOpen on:add-feeds />

<style>
  .section-heading {
    margin: 0 0 0.5rem;
  }

  .availability {
    white-space: nowrap;
  }

  .expanded-row {
    padding: 0.5rem 0;
  }

  .expanded-row :global(.bx--list--unordered > .bx--list__item::before) {
    content: none;
  }

  .path {
    display: flex;
    flex-direction: column;
    margin-bottom: 0.5rem;
    padding: 0.3rem 0.6rem;
    font-size: 0.8rem;
    background-color: var(--cds-background-selected-hover);
    overflow-y: auto;
    white-space: nowrap;
    max-width: 95%;
    user-select: all;
    cursor: text;
  }

  .error {
    color: var(--cds-support-error);
  }
</style>
